"use client";

import React from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export type ApplicationAnswer = "" | "yes" | "no";

export interface ApplicationAnswers {
  termination: ApplicationAnswer;
  owner: ApplicationAnswer;
}

export const EMPTY_APPLICATION_ANSWERS: ApplicationAnswers = { termination: "", owner: "" };

interface ApplicationQuestionsSectionProps {
  petName: string;
  /** Broker funnel: the questions are about the agent's customer, not "you". */
  isBroker?: boolean;
  terminationAnswer: ApplicationAnswer;
  ownerAnswer: ApplicationAnswer;
  terminationError?: string;
  ownerError?: string;
  disabled?: boolean;
  onTerminationChange: (v: ApplicationAnswer) => void;
  onOwnerChange: (v: ApplicationAnswer) => void;
}

interface QuestionProps {
  id: string;
  label: string;
  value: ApplicationAnswer;
  error?: string;
  disabled?: boolean;
  onChange: (v: ApplicationAnswer) => void;
}

const Question: React.FC<QuestionProps> = ({ id, label, value, error, disabled, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-2 field-row">
      <Label htmlFor={id} className="leading-snug">{label}</Label>
      {/* One cell for field + messages: as separate grid children the messages would land below the (tall) label row. */}
      <div className="space-y-1">
        <Select value={value} onValueChange={(v) => onChange(v as ApplicationAnswer)} disabled={disabled}>
          <SelectTrigger id={id} className={cn(error && "border-destructive focus-visible:ring-destructive")}>
            <SelectValue placeholder={t("contactDetails.applicationQuestions.select")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">{t("common.yes")}</SelectItem>
            <SelectItem value="no">{t("common.no")}</SelectItem>
          </SelectContent>
        </Select>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </div>
  );
};

/** The two Antragsfragen on the personal-details page. One "wrong" answer ends the funnel for this customer: on "Weiter"
 * the form shows the rejection as the field's error and does not let them continue. */
const ApplicationQuestionsSection: React.FC<ApplicationQuestionsSectionProps> = ({
  petName,
  isBroker = false,
  terminationAnswer,
  ownerAnswer,
  terminationError,
  ownerError,
  disabled,
  onTerminationChange,
  onOwnerChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-4">
      <h3 className="pt-4 text-xl font-semibold">{t("contactDetails.applicationQuestions.title")}</h3>
      <Question
        id="applicationQuestionTermination"
        label={t(isBroker ? "contactDetails.applicationQuestions.terminationLabelBroker" : "contactDetails.applicationQuestions.terminationLabel")}
        value={terminationAnswer}
        error={terminationError}
        disabled={disabled}
        onChange={onTerminationChange}
      />
      <Question
        id="applicationQuestionOwner"
        label={t(isBroker ? "contactDetails.applicationQuestions.ownerLabelBroker" : "contactDetails.applicationQuestions.ownerLabel", { petName })}
        value={ownerAnswer}
        error={ownerError}
        disabled={disabled}
        onChange={onOwnerChange}
      />
    </div>
  );
};

export default ApplicationQuestionsSection;
